"use client";

import { useSelector } from "react-redux";
import { useMemo } from "react";

import useRouter from "@stadium/hooks/lib/use-custom-route";

import BreadcrumbContainer from "@stadium/ui/src/components/breadcrumb-container";

import {
  getCatalogEntriesPageUrl,
  getCatalogNavLinkUrl,
  getCategoryUrl,
  getHeaderNavLinkUrl,
} from "@stadium/shared/src/helpers/store/links";
import { ALL_PRODUCTS } from "@constants/routes";
import { hyphenatedStringToReadable } from "@stadium/helpers/string";
import { createMenuCodeBreadcrumbNamesMap } from "@helpers/menu";
import {
  BREADCRUMB_BRAND,
  BREADCRUMB_CATCH_ALL,
  BREADCRUMB_COLLECTION,
  BREADCRUMB_LISTING,
  BREADCRUMB_SEARCH,
} from "@constants/breadcrumbs";

import { getMenu } from "@selectors/menu";
import { getIsNewRouteEnabled, getShowBreadcrumbsOnProductListing } from "@selectors/store";
import { useStoreContext } from "@components/store/provider";

import styles from "./main.module.scss";

interface BreadcrumbItem {
  name: string;
  link: string;
}

interface RouteQuery {
  countryCode?: string;
  storeIdentifier?: string;
  workspaceSlug?: string;
  categorySlug?: string;
  category?: string;
  catchAll?: string[];
  searchQuery?: string;
  brandSlug?: string;
  isNewRouteEnabled?: boolean;
}

interface MenuBreadcrumbSegment {
  code: string;
  name: string;
  depth: number;
  hasChildren: boolean;
}

export const HOME_PAGE_LABEL = "Home";
export const CATALOG_PAGE_LABEL = "Catalog";
export const ALL_PRODUCTS_LABEL = "All Products";

function buildCatchAllBreadcrumbs(catchAll: string[], baseUrl: string): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [{ name: HOME_PAGE_LABEL, link: baseUrl }];
  let path = "";
  for (const segment of catchAll) {
    path += `/${segment}`;
    items.push({
      name: hyphenatedStringToReadable(segment),
      link: `${baseUrl}${path}`,
    });
  }
  return items;
}

const SPECIAL_LISTING_BREADCRUMB_TYPES = new Set([
  BREADCRUMB_SEARCH,
  BREADCRUMB_BRAND,
  BREADCRUMB_COLLECTION,
]);

function buildBaseCatalogItems(
  queryForRoutePath: RouteQuery,
  isNewRouteEnabled?: boolean,
): BreadcrumbItem[] {
  return [
    { name: HOME_PAGE_LABEL, link: getHeaderNavLinkUrl(queryForRoutePath) },
    {
      name: CATALOG_PAGE_LABEL,
      link: getCatalogNavLinkUrl({ queryForRoutePath, isNewRouteEnabled }),
    },
  ];
}

function getAllProductsBreadcrumbItem(
  queryForRoutePath: RouteQuery,
  isNewRouteEnabled?: boolean,
): BreadcrumbItem {
  return {
    name: ALL_PRODUCTS_LABEL,
    link: getCategoryUrl({ category: ALL_PRODUCTS, queryForRoutePath, isNewRouteEnabled }),
  };
}

function getMenuCrumbLink(
  crumb: MenuBreadcrumbSegment,
  queryForRoutePath: RouteQuery,
  isNewRouteEnabled?: boolean,
): string {
  if (crumb.depth === 1 && crumb.hasChildren) {
    return getCatalogEntriesPageUrl({
      categorySlug: crumb.code,
      queryForRoutePath,
      isNewRouteEnabled,
    });
  }
  return getCategoryUrl({
    category: crumb.code,
    queryForRoutePath,
    isNewRouteEnabled,
  });
}

function appendMenuCategoryBreadcrumbs(
  items: BreadcrumbItem[],
  categorySlug: string,
  menuCodeBreadcrumbNamesMap: Record<string, MenuBreadcrumbSegment[]>,
  queryForRoutePath: RouteQuery,
  isNewRouteEnabled?: boolean,
): BreadcrumbItem[] {
  const breadCrumbs = (menuCodeBreadcrumbNamesMap[categorySlug] ?? []).slice(1);

  breadCrumbs.forEach((crumb) => {
    items.push({
      name: crumb.name,
      link: getMenuCrumbLink(crumb, queryForRoutePath, isNewRouteEnabled),
    });
  });

  return items;
}

function appendSpecialListingTerminalCrumb(
  items: BreadcrumbItem[],
  breadcrumbPageType: string,
  {
    searchQuery,
    brandSlug,
    menuIdentifier,
  }: {
    searchQuery?: string;
    brandSlug?: string;
    menuIdentifier?: string;
  },
): void {
  if (breadcrumbPageType === BREADCRUMB_SEARCH) {
    items.push({
      name: searchQuery ? `Search: ${decodeURIComponent(searchQuery)}` : "Search Results",
      link: "",
    });
    return;
  }

  if (breadcrumbPageType === BREADCRUMB_BRAND) {
    items.push({
      name: brandSlug ? `${hyphenatedStringToReadable(decodeURIComponent(brandSlug))}` : "",
      link: "",
    });
    return;
  }

  if (breadcrumbPageType === BREADCRUMB_COLLECTION) {
    items.push({
      name: menuIdentifier
        ? `${hyphenatedStringToReadable(decodeURIComponent(menuIdentifier))}`
        : "",
      link: "",
    });
  }
}

function buildSpecialListingBreadcrumbs(
  items: BreadcrumbItem[],
  breadcrumbPageType: string,
  queryForRoutePath: RouteQuery,
  isNewRouteEnabled: boolean | undefined,
  options: { searchQuery?: string; brandSlug?: string; menuIdentifier?: string },
): BreadcrumbItem[] | null {
  if (!SPECIAL_LISTING_BREADCRUMB_TYPES.has(breadcrumbPageType)) {
    return null;
  }

  items.push(getAllProductsBreadcrumbItem(queryForRoutePath, isNewRouteEnabled));
  appendSpecialListingTerminalCrumb(items, breadcrumbPageType, options);
  return items;
}

function buildCatalogBreadcrumbs(
  query: RouteQuery,
  menuCodeBreadcrumbNamesMap: Record<string, MenuBreadcrumbSegment[]>,
  {
    breadcrumbPageType,
    menuIdentifier,
    isNewRouteEnabled,
  }: {
    breadcrumbPageType: string;
    menuIdentifier?: string;
    isNewRouteEnabled?: boolean;
  },
): BreadcrumbItem[] {
  const { countryCode, storeIdentifier, workspaceSlug, brandSlug, searchQuery } = query;
  if (!countryCode || !storeIdentifier) return [];

  const queryForRoutePath: RouteQuery = {
    countryCode,
    storeIdentifier,
    ...(workspaceSlug && { workspaceSlug }),
  };
  const categorySlug = query.categorySlug || query.category;

  const items = buildBaseCatalogItems(queryForRoutePath, isNewRouteEnabled);

  const specialListingItems = buildSpecialListingBreadcrumbs(
    items,
    breadcrumbPageType,
    queryForRoutePath,
    isNewRouteEnabled,
    { searchQuery, brandSlug, menuIdentifier },
  );
  if (specialListingItems) {
    return specialListingItems;
  }

  if (categorySlug === ALL_PRODUCTS) {
    items.push(getAllProductsBreadcrumbItem(queryForRoutePath, isNewRouteEnabled));
    return items;
  }

  if (!categorySlug) {
    return items;
  }

  return appendMenuCategoryBreadcrumbs(
    items,
    categorySlug,
    menuCodeBreadcrumbNamesMap,
    queryForRoutePath,
    isNewRouteEnabled,
  );
}

function shouldRenderBreadcrumbs(
  breadcrumbPageType: string | null,
  showBreadcrumbsOnProductListing?: boolean,
): breadcrumbPageType is string {
  if (!breadcrumbPageType) {
    return false;
  }

  return !(breadcrumbPageType === BREADCRUMB_LISTING && !showBreadcrumbsOnProductListing);
}

function buildCatchAllBreadcrumbItems(query: any, queryForRoutePath: any): BreadcrumbItem[] {
  const { catchAll } = query;
  if (!Array.isArray(catchAll) || catchAll.length === 0) {
    return [];
  }

  const baseUrl = getHeaderNavLinkUrl(queryForRoutePath);
  return buildCatchAllBreadcrumbs(catchAll, baseUrl);
}

function buildBreadcrumbItems({
  query,
  menuCodeBreadcrumbNamesMap,
  showBreadcrumbsOnProductListing,
  isNewRouteEnabled,
  breadcrumbPageType,
  menuIdentifier,
}: {
  query: RouteQuery;
  menuCodeBreadcrumbNamesMap: Record<string, MenuBreadcrumbSegment[]>;
  showBreadcrumbsOnProductListing?: boolean;
  isNewRouteEnabled?: boolean;
  breadcrumbPageType: string | null;
  menuIdentifier?: string;
}): BreadcrumbItem[] {
  if (!shouldRenderBreadcrumbs(breadcrumbPageType, showBreadcrumbsOnProductListing)) {
    return [];
  }

  const queryForRoutePath: RouteQuery = {
    countryCode: query.countryCode,
    storeIdentifier: query.storeIdentifier,
    isNewRouteEnabled,
  };

  if (breadcrumbPageType === BREADCRUMB_CATCH_ALL) {
    return buildCatchAllBreadcrumbItems(query, queryForRoutePath);
  }

  return buildCatalogBreadcrumbs(query, menuCodeBreadcrumbNamesMap, {
    breadcrumbPageType,
    menuIdentifier,
    isNewRouteEnabled,
  });
}

const BreadCrumbs = () => {
  const { query } = useRouter();
  const showBreadcrumbsOnProductListing = useSelector(getShowBreadcrumbsOnProductListing);
  const orderedMenuItem = useSelector(getMenu);
  const isNewRouteEnabled = useSelector(getIsNewRouteEnabled);
  const { breadcrumbPageType, menuIdentifier } = useStoreContext();

  const menuCodeBreadcrumbNamesMap = useMemo(
    () =>
      createMenuCodeBreadcrumbNamesMap(orderedMenuItem) as Record<string, MenuBreadcrumbSegment[]>,
    [orderedMenuItem],
  );

  const items = useMemo(
    () =>
      buildBreadcrumbItems({
        query: (query ?? {}) as RouteQuery,
        menuCodeBreadcrumbNamesMap,
        showBreadcrumbsOnProductListing,
        isNewRouteEnabled,
        breadcrumbPageType,
        menuIdentifier,
      }),
    [
      query,
      menuCodeBreadcrumbNamesMap,
      showBreadcrumbsOnProductListing,
      isNewRouteEnabled,
      breadcrumbPageType,
      menuIdentifier,
    ],
  );

  if (items.length === 0) return null;

  return (
    <div className={styles.storePageContainer}>
      <BreadcrumbContainer items={items} className="store-breadcrumb" />
    </div>
  );
};

export default BreadCrumbs;
